import * as React from "react";
import { cn } from "../../lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Optional leading icon rendered inside the input field. */
  startIcon?: React.ReactNode;
  /** Optional trailing icon rendered inside the input field. */
  endIcon?: React.ReactNode;
}

/**
 * Input — Clones Design System (ADR-0030).
 *
 * Surfaces use --paper / --ink tokens. Placeholder uses --ink-4 (tertiary).
 * Focus ring uses --pulse with paper offset (matches global :focus-visible).
 * `.ru-text` is applied so the Cyrillic-tuned line-height kicks in for any
 * Russian placeholder/value the input carries.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, startIcon, endIcon, ...props }, ref) => {
    if (startIcon || endIcon) {
      return (
        <div className="relative flex items-center">
          {startIcon && (
            <span className="pointer-events-none absolute left-[var(--field-padding-x)] flex items-center text-[var(--ink-3)]">
              {startIcon}
            </span>
          )}
          <input
            data-cds-input
            type={type}
            className={cn(
              "ru-text",
              "flex h-[var(--control-size)] min-h-[var(--control-size)] w-full rounded-[var(--r-3)] border border-[var(--border-control)] bg-[var(--paper-2)] py-[var(--field-padding-y)] text-[length:var(--field-font-size)] text-[var(--ink)]",
              "[@media(any-pointer:coarse)]:min-h-[44px] [@media(any-pointer:coarse)]:text-[16px]",
              "[@media(max-width:639px)]:min-h-[44px] [@media(max-width:639px)]:text-[16px]",
              "placeholder:text-[var(--ink-4)]",
              "aria-[invalid=true]:border-[var(--bad)] aria-[invalid=true]:ring-[var(--bad)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pulse)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--paper)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
              startIcon ? "pl-[calc(var(--field-padding-x)+24px)]" : "pl-[var(--field-padding-x)]",
              endIcon ? "pr-[calc(var(--field-padding-x)+24px)]" : "pr-[var(--field-padding-x)]",
              className,
            )}
            ref={ref}
            {...props}
          />
          {endIcon && (
            <span className="absolute right-[var(--field-padding-x)] flex items-center text-[var(--ink-3)]">
              {endIcon}
            </span>
          )}
        </div>
      );
    }

    return (
      <input
        data-cds-input
        type={type}
        className={cn(
          "ru-text",
          "flex h-[var(--control-size)] min-h-[var(--control-size)] w-full rounded-[var(--r-3)] border border-[var(--border-control)] bg-[var(--paper-2)] px-[var(--field-padding-x)] py-[var(--field-padding-y)] text-[length:var(--field-font-size)] text-[var(--ink)]",
          "[@media(any-pointer:coarse)]:min-h-[44px] [@media(any-pointer:coarse)]:text-[16px]",
          "[@media(max-width:639px)]:min-h-[44px] [@media(max-width:639px)]:text-[16px]",
          "placeholder:text-[var(--ink-4)]",
          "aria-[invalid=true]:border-[var(--bad)] aria-[invalid=true]:ring-[var(--bad)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pulse)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--paper)]",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
